import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Member, Publication } from '../types';
import apiService from '../services/api';
import { ArrowLeftIcon, AcademicCapIcon, BriefcaseIcon, TrophyIcon } from '@heroicons/react/24/outline';

const MemberDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [member, setMember] = useState<Member | null>(null);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMemberDetail = async () => {
      try {
        const [memberRes, publicationsRes] = await Promise.all([
          apiService.get<Member>(`/members/${id}`),
          apiService.get<Publication[]>(`/members/${id}/publications`)
        ]);
        
        if (memberRes.data) setMember(memberRes.data);
        if (publicationsRes.data) setPublications(publicationsRes.data);
      } catch (error) {
        console.error('获取成员详情失败:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMemberDetail();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">加载中...</div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">成员不存在</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* 返回按钮 */}
      <Link
        to="/members"
        className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        返回成员列表
      </Link>

      {/* 成员基本信息 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
        <div className="flex flex-col md:flex-row items-start space-y-6 md:space-y-0 md:space-x-8">
          {/* 头像 */}
          <div className="flex-shrink-0">
            <img
              src={member.avatar || '/default-avatar.png'}
              alt={member.name}
              className="w-32 h-32 rounded-full object-cover border-4 border-primary-100"
            />
          </div>

          {/* 基本信息 */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{member.name}</h1>
            <p className="text-xl text-primary-600 mb-6">{member.title}</p>

            {/* 联系信息 */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center text-gray-600">
                <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                </svg>
                <span>{member.email}</span>
              </div>
              {member.phone && (
                <div className="flex items-center text-gray-600">
                  <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span>{member.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 个人简介 */}
      {member.bio && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <svg className="h-5 w-5 mr-2 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            个人简介
          </h2>
          <div 
            className="prose-rich-text"
            dangerouslySetInnerHTML={{ __html: member.bio }}
          />
        </div>
      )}

      {/* 研究方向 */}
      {member.researchInterests && member.researchInterests.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">研究方向</h2>
          <div className="flex flex-wrap gap-2">
            {member.researchInterests.map((interest, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
              >
                {interest}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 教育背景 */}
      {member.education && member.education.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <AcademicCapIcon className="h-5 w-5 mr-2" />
            教育背景
          </h2>
          <div className="space-y-3">
            {member.education.map((edu, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                <div>
                  <div className="font-medium">{edu.degree}</div>
                  <div className="text-gray-600">{edu.institution}</div>
                </div>
                <div className="text-gray-500">{edu.year}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 工作经历 */}
      {member.experience && member.experience.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <BriefcaseIcon className="h-5 w-5 mr-2" />
            工作经历
          </h2>
          <div className="space-y-3">
            {member.experience.map((exp, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                <div>
                  <div className="font-medium">{exp.position}</div>
                  <div className="text-gray-600">{exp.institution}</div>
                </div>
                <div className="text-gray-500">
                  {exp.startYear} - {exp.current ? '至今' : exp.endYear}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 获奖情况 */}
      {member.awards && member.awards.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <TrophyIcon className="h-5 w-5 mr-2" />
            获奖情况
          </h2>
          <div className="space-y-3">
            {member.awards.map((award, index) => (
              <div key={index} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
                <div>
                  <div className="font-medium">{award.name}</div>
                  <div className="text-gray-600">{award.description}</div>
                </div>
                <div className="text-gray-500">{award.year}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 发表论文 */}
      {publications.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-4">发表论文</h2>
          <div className="space-y-4">
            {publications.map((pub) => (
              <div key={pub._id} className="border-l-4 border-primary-500 pl-4">
                <h3 className="font-medium text-gray-900">{pub.title}</h3>
                <p className="text-gray-600 text-sm mt-1">
                  {typeof pub.authors === 'string' 
                    ? pub.authors 
                    : pub.authors.map(a => a.name).join(', ')
                  }
                </p>
                <p className="text-gray-500 text-sm mt-1">
                  {pub.journal}, {pub.year}
                  {pub.doi && (
                    <span className="ml-2">
                      DOI: <a href={`https://doi.org/${pub.doi}`} className="text-primary-600 hover:underline">{pub.doi}</a>
                    </span>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberDetail; 